import { CategoryKey } from '#dictionary'
import type { FinCategoryKey } from '#dictionary/finCategories'

import { FinancialRecord } from '../financialRecords'
import createTimelineRecord from '../timeline/createTimelineRecord'
import type { BaseTimelineRecordPayload } from '../timeline/TimelineRecord'
import type { TimelineRecord } from '../timeline/TimelineRecord'
import TimelineGenerator from './TimelineGenerator'
import type { GenerationResult } from './types'

// `financialRecord` is always the generator's own `this.record`, so the
// timeline factory always fills it in — callers just supply category
// info. Mirrors the two-branch discrimination on `TimelineRecordPayload`:
// either `categoryKey` is set (explicit) or it isn't (inferred from the
// injected `financialRecord`).
type CreateRecordProps =
  | (BaseTimelineRecordPayload & { categoryKey: FinCategoryKey })
  | (BaseTimelineRecordPayload & { categoryKey?: never })

class LiabilityGenerator extends TimelineGenerator {
  private readonly recurringPayment: number

  constructor(record: FinancialRecord, _children: FinancialRecord[]) {
    super(record)
    this.recurringPayment = record.recurringValue ?? 0
  }

  // The starting balance for `year` is the previous year's ending balance
  // (base class stores `startYear - 1` = `annualValue`). Reading it fresh each
  // call keeps `_generate` idempotent, so `calculateForYearWithDrawdowns`
  // retries do not over-amortize the loan by one year per retry.
  _generate(year: number): GenerationResult {
    const [startYear, endYear] = this.record.actualYearsRange
    const openingBalance = this.previousYearFinalValue

    if (year < startYear || year > endYear) {
      return { timeline: [], finalValue: openingBalance }
    }

    if (openingBalance <= 0) {
      return { timeline: [], finalValue: 0 }
    }

    const isLastYear = year === endYear
    const interestAccrued = openingBalance * this.record.growthRate
    const principalDue = this.recurringPayment - interestAccrued

    const isBalloonPayment = isLastYear && principalDue < openingBalance
    const principalPaid = isBalloonPayment
      ? openingBalance
      : Math.min(Math.max(principalDue, 0), openingBalance)
    const interestPaid = isBalloonPayment
      ? interestAccrued
      : Math.min(interestAccrued, this.recurringPayment)
    const balanceAfterPayment =
      openingBalance + interestAccrued - this.recurringPayment
    const remainingBalance = isBalloonPayment
      ? 0
      : Math.max(0, balanceAfterPayment)

    const timeline = [
      this.createRecord({
        type: 'liabilityClosure',
        year,
        value: remainingBalance,
        meta: { isBalloonPayment },
      }),
      this.createRecord({
        type: 'interestPayment',
        year,
        value: interestPaid,
        meta: {
          balanceOpen: openingBalance,
          rate: this.record.growthRate,
        },
        categoryKey: CategoryKey.catExpensesCreditInterest,
      }),
      this.createRecord({
        type: 'principalPayment',
        year,
        value: principalPaid,
        meta: { interest: interestPaid, cappedAt: openingBalance },
        categoryKey: CategoryKey.catExpensesCreditPrincipal,
      }),
    ]

    return { timeline, finalValue: remainingBalance }
  }

  private createRecord(props: CreateRecordProps): TimelineRecord {
    return createTimelineRecord({ ...props, financialRecord: this.record })
  }
}

export default LiabilityGenerator
